import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Button } from 'foundations-components/transitional/components/Button';
import { Checkbox } from 'foundations-components/transitional/components/Checkbox';
import { TextArea } from 'foundations-components/transitional/components/TextArea';
import { ThumbsDownIcon } from 'foundations-assets/icons/components/ThumbsDownIcon';
import { ThumbsUpIcon } from 'foundations-assets/icons/components/ThumbsUpIcon';
import styled from 'foundations-theming/utils';
import { useCallback, useState } from 'react';
import { isFunction } from '../../utils/TypeUtils';
import { ActionIconButton } from './internal/ActionIconButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const POSITIVE_FEEDBACK = 1;
const NEGATIVE_FEEDBACK = -1;
const DEFAULT_NEGATIVE_FEEDBACK_OPTIONS = [{
  label: /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.options.notRelevant"
  }),
  value: 'NOT_RELEVANT'
}, {
  label: /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.options.incomplete"
  }),
  value: 'INCOMPLETE'
}, {
  label: /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.options.inaccurate"
  }),
  value: 'INACCURATE'
}, {
  label: /*#__PURE__*/_jsx(FormattedReactMessage, {
    message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.options.other"
  }),
  value: 'OTHER'
}];
const ExpandableContainer = styled.div.withConfig({
  displayName: "InlineFeedbackExpandable__ExpandableContainer"
})(["max-height:", ";opacity:", ";overflow:hidden;transition:max-height 0.3s cubic-bezier(0.76,0,0.24,1),opacity 0.3s cubic-bezier(0.76,0,0.24,1);"], ({
  isExpanded
}) => isExpanded ? '500px' : '0', props => props.isExpanded ? 1 : 0);
const InlineFeedbackExpandable = ({
  initialVote,
  onPositiveFeedback,
  onNegativeFeedback,
  onRemoveFeedback,
  negativeFeedbackTitle,
  negativeFeedbackBody,
  negativeFeedbackBodyOptions,
  negativeFeedbackTextAreaPlaceholder
}) => {
  const [draftVote, setDraftVote] = useState(initialVote);
  const [feedbackExpanded, setFeedbackExpanded] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [messageFeedback, setMessageFeedback] = useState('');
  const handlePositiveFeedback = useCallback(() => {
    const isPositiveFeedbackSelected = draftVote === POSITIVE_FEEDBACK;
    if (isPositiveFeedbackSelected) {
      setDraftVote(null);
      if (isFunction(onRemoveFeedback)) {
        onRemoveFeedback({
          previousVote: POSITIVE_FEEDBACK
        });
      }
    } else {
      setDraftVote(POSITIVE_FEEDBACK);
      // Hide feedback form when positive feedback is selected
      setFeedbackExpanded(false);
      if (isFunction(onPositiveFeedback)) {
        onPositiveFeedback();
      }
    }
  }, [draftVote, onPositiveFeedback, onRemoveFeedback]);
  const handleNegativeFeedbackClick = useCallback(() => {
    const isNegativeFeedbackSelected = draftVote === NEGATIVE_FEEDBACK;
    if (isNegativeFeedbackSelected) {
      setDraftVote(null);
      setFeedbackExpanded(false);
      if (isFunction(onRemoveFeedback)) {
        onRemoveFeedback({
          previousVote: NEGATIVE_FEEDBACK
        });
      }
    } else {
      setDraftVote(NEGATIVE_FEEDBACK);
      setFeedbackExpanded(true);
      if (isFunction(onNegativeFeedback)) {
        onNegativeFeedback({});
      }
    }
  }, [draftVote, onRemoveFeedback, onNegativeFeedback]);
  const handleMessageFeedbackChange = useCallback(({
    target: {
      value
    }
  }) => {
    setMessageFeedback(value);
  }, []);
  const handleMessageFeedbackOptionsChange = useCallback(option => {
    if (selectedOptions.includes(option)) {
      setSelectedOptions(selectedOptions.filter(e => e !== option));
    } else {
      setSelectedOptions([...selectedOptions, option]);
    }
  }, [selectedOptions]);
  const resetInputFields = useCallback(() => {
    setSelectedOptions([]);
    setMessageFeedback('');
  }, []);
  const handleSubmit = useCallback(() => {
    setFeedbackExpanded(false);
    if (isFunction(onNegativeFeedback)) {
      onNegativeFeedback({
        options: selectedOptions,
        feedbackMessage: messageFeedback
      });
    }
    resetInputFields();
  }, [onNegativeFeedback, selectedOptions, messageFeedback, resetInputFields]);
  const handleCancel = useCallback(() => {
    setFeedbackExpanded(false);
    setDraftVote(null);
    resetInputFields();
  }, [resetInputFields]);
  const canSubmit = selectedOptions.length > 0 || messageFeedback.length > 0;
  return /*#__PURE__*/_jsxs(View, {
    children: [/*#__PURE__*/_jsxs(Flex, {
      children: [/*#__PURE__*/_jsx(ActionIconButton, {
        "aria-pressed": draftVote === POSITIVE_FEEDBACK,
        "data-test-id": "positive-feedback-button",
        selected: draftVote === POSITIVE_FEEDBACK,
        onClick: handlePositiveFeedback,
        use: "tertiary-extra-light",
        size: "sm",
        children: /*#__PURE__*/_jsx(ThumbsUpIcon, {})
      }), /*#__PURE__*/_jsx(ActionIconButton, {
        "aria-pressed": draftVote === NEGATIVE_FEEDBACK,
        "data-test-id": "negative-feedback-button",
        selected: draftVote === NEGATIVE_FEEDBACK,
        onClick: handleNegativeFeedbackClick,
        use: "tertiary-extra-light",
        size: "sm",
        children: /*#__PURE__*/_jsx(ThumbsDownIcon, {})
      })]
    }), /*#__PURE__*/_jsx(ExpandableContainer, {
      isExpanded: feedbackExpanded,
      children: /*#__PURE__*/_jsxs(Flex, {
        marginBlockStart: "300",
        gap: "250",
        flexDirection: "column",
        children: [/*#__PURE__*/_jsx(Text, {
          typography: "heading-50",
          children: negativeFeedbackTitle || /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.dialogHeader"
          })
        }), !negativeFeedbackBody && /*#__PURE__*/_jsx(FormattedMessage, {
          message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.optionSelectHeader"
        }), negativeFeedbackBody || /*#__PURE__*/_jsx(_Fragment, {
          children: /*#__PURE__*/_jsx(Flex, {
            flexDirection: "column",
            alignItems: "stretch",
            children: (negativeFeedbackBodyOptions || DEFAULT_NEGATIVE_FEEDBACK_OPTIONS).map(option => /*#__PURE__*/_jsx(Checkbox, {
              checked: selectedOptions.includes(option.value),
              onChange: () => handleMessageFeedbackOptionsChange(option.value),
              children: /*#__PURE__*/_jsx(Text, {
                typography: "detail-200",
                children: option.label
              })
            }, String(option.value)))
          })
        }), /*#__PURE__*/_jsx(TextArea, {
          name: "feedback",
          use: "on-dark",
          rows: 1,
          typography: "body-100",
          resize: true,
          placeholder: negativeFeedbackTextAreaPlaceholder || I18n.text('aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.freeformPlaceholder'),
          onChange: handleMessageFeedbackChange,
          value: messageFeedback
        }), /*#__PURE__*/_jsxs(Flex, {
          justifyContent: "end",
          children: [/*#__PURE__*/_jsx(Button, {
            onClick: handleCancel,
            margin: "0",
            marginInlineEnd: "200",
            type: "reset",
            use: "secondary",
            size: "extra-small",
            responsive: false,
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.buttons.cancel"
            })
          }), /*#__PURE__*/_jsx(Button, {
            onClick: handleSubmit,
            margin: "0",
            type: "submit",
            use: "primary",
            size: "extra-small",
            disabled: !canSubmit,
            responsive: false,
            children: /*#__PURE__*/_jsx(FormattedReactMessage, {
              message: "aiComponentsUiLibrary.components.inlineFeedback.messageFeedback.buttons.submit"
            })
          })]
        })]
      })
    })]
  });
};
InlineFeedbackExpandable.displayName = 'InlineFeedbackExpandable';
export default InlineFeedbackExpandable;